import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { useUser } from '@shared/hooks/useUser';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import {
  Plus,
  List,
  Grid,
  Clock,
  AlertTriangle,
  Play,
  Pause,
  Trash2,
  X,
  Edit2,
} from 'lucide-react';
import { ScheduleIntervalSelect } from '@features/backups/components/ScheduleIntervalSelect';
import { instantToScheduleTime, scheduleTimeToInstant } from '../utils/scheduleTime';
import { useCronValidation } from '@features/backups/hooks/useCronValidation';

interface BackupConfig {
  id: string;
  name: string;
  type: string;
  agent_name?: string;
}

interface Schedule {
  id: string;
  backup_config_id: string;
  schedule_type: string;
  interval_value?: string;
  cron_expression?: string;
  onetime_run_at?: string;
  timezone: string;
  priority: number;
  is_enabled: boolean;
  next_run_at: string;
  created_at: string;
  updated_at: string;
  backup_config_name?: string;
  agent_name?: string;
}

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'UTC', key: 'utc' },
  { value: 'America/New_York', key: 'eastern' },
  { value: 'America/Chicago', key: 'central' },
  { value: 'America/Denver', key: 'mountain' },
  { value: 'America/Los_Angeles', key: 'pacific' },
  { value: 'America/Sao_Paulo', key: 'saoPaulo' },
  { value: 'Europe/London', key: 'london' },
  { value: 'Europe/Paris', key: 'paris' },
  { value: 'Europe/Berlin', key: 'berlin' },
  { value: 'Asia/Tokyo', key: 'tokyo' },
  { value: 'Asia/Shanghai', key: 'shanghai' },
  { value: 'Asia/Singapore', key: 'singapore' },
  { value: 'Australia/Sydney', key: 'sydney' },
  { value: 'Pacific/Auckland', key: 'auckland' },
];

export default function Schedules() {
  const { t, i18n } = useTranslation('backups');
  const locale = i18n.resolvedLanguage || i18n.language;
  const { showConfirm } = useDialog();
  const { user } = useUser();
  const browserTimezone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  })();
  const defaultTimezone = user?.timezone || browserTimezone;

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [backupConfigs, setBackupConfigs] = useState<BackupConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);

  // View State
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [collapsedAgents, setCollapsedAgents] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [listError, setListError] = useState(false);
  const [configsError, setConfigsError] = useState(false);
  const [configsLoading, setConfigsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const timezoneTouchedRef = useRef(false);

  // Form State
  const [selectedBackupConfig, setSelectedBackupConfig] = useState('');
  const [scheduleType, setScheduleType] = useState<'INTERVAL' | 'CRON' | 'ONETIME'>('INTERVAL');
  const [intervalValue, setIntervalValue] = useState('6h');
  const [cronExpression, setCronExpression] = useState('0 2 * * *');
  const [, setCronExplanation] = useState('');
  const [oneTimeRunAt, setOneTimeRunAt] = useState('');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [priority, setPriority] = useState(5);
  const [isEnabled, setIsEnabled] = useState(true);

  const [saving, setSaving] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const scheduleDialogRef = useRef<HTMLDivElement>(null);
  const savingRef = useRef(saving);
  savingRef.current = saving;
  useEffect(() => {
    if (!showCreateModal && !showEditModal) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = scheduleDialogRef.current;
    dialog?.querySelector<HTMLElement>('select:not(:disabled)')?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!savingRef.current) {
          setShowCreateModal(false);
          setShowEditModal(false);
        }
      }
      if (event.key !== 'Tab' || !dialog) return;
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button, input, select, textarea, a[href], [tabindex="0"]'
        )
      ).filter((el) => !el.matches(':disabled') && el.offsetParent !== null);
      const first = controls[0],
        last = controls[controls.length - 1];
      if (!first || !last) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        (!event.shiftKey && document.activeElement === last) ||
        !dialog.contains(document.activeElement)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus();
    };
  }, [showCreateModal, showEditModal]);
  const [conflictWarnings, setConflictWarnings] = useState<{ message: string }[]>([]);
  const [acknowledgedConflicts, setAcknowledgedConflicts] = useState(false);

  const formatDateTime = (value: string, timeZone: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    try {
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'short',
        timeStyle: 'medium',
        timeZone,
      }).format(date);
    } catch {
      return value;
    }
  };

  const formatScheduleType = (type: string) => {
    const typeKeys: Record<string, string> = {
      INTERVAL: 'types.interval',
      CRON: 'types.cron',
      ONETIME: 'types.oneTime',
    };
    const key = typeKeys[type.toUpperCase()];
    return key ? t(`schedules.${key}`) : type;
  };

  // CRON validation hook
  const cronValidation = useCronValidation(cronExpression, scheduleType === 'CRON');

  useEffect(() => {
    fetchSchedules();
    fetchBackupConfigs();
  }, []);

  useEffect(() => {
    if (showCreateModal && !timezoneTouchedRef.current) {
      setTimezone(defaultTimezone);
    }
  }, [defaultTimezone, showCreateModal]);

  const checkConflicts = useCallback(async () => {
    setAcknowledgedConflicts(false);
    try {
      const params = new URLSearchParams({
        backup_config_id: selectedBackupConfig,
        schedule_type: scheduleType,
        timezone,
      });
      if (scheduleType === 'INTERVAL') params.append('interval_value', intervalValue);
      else if (scheduleType === 'CRON') params.append('cron_expression', cronExpression);
      else if (scheduleType === 'ONETIME' && oneTimeRunAt)
        params.append('onetime_run_at', scheduleTimeToInstant(oneTimeRunAt, timezone));
      if (selectedSchedule) params.append('exclude_schedule_id', selectedSchedule.id);

      const res = await fetch(`/api/v1/schedules/check-conflicts?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConflictWarnings(data.has_conflicts ? data.conflicts || [] : []);
      }
    } catch {
      // Ignore errors
    }
  }, [
    selectedBackupConfig,
    scheduleType,
    timezone,
    intervalValue,
    cronExpression,
    oneTimeRunAt,
    selectedSchedule,
  ]);

  // Effects for cron/conflicts
  useEffect(() => {
    if (scheduleType === 'CRON') {
      const timer = setTimeout(() => fetchCronExplanation(cronExpression), 500);
      return () => clearTimeout(timer);
    } else setCronExplanation('');
  }, [cronExpression, scheduleType]);

  useEffect(() => {
    if ((showCreateModal || showEditModal) && selectedBackupConfig && scheduleType) {
      if (scheduleType === 'ONETIME' && !oneTimeRunAt) return;
      const timer = setTimeout(() => checkConflicts(), 800);
      return () => clearTimeout(timer);
    } else {
      setConflictWarnings([]);
      setAcknowledgedConflicts(false);
    }
  }, [
    selectedBackupConfig,
    scheduleType,
    intervalValue,
    cronExpression,
    oneTimeRunAt,
    timezone,
    showCreateModal,
    showEditModal,
    checkConflicts,
  ]);

  const fetchSchedules = async () => {
    setLoading(true);
    setListError(false);
    try {
      const res = await fetch('/api/v1/schedules', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Schedule load failed');
      const data = await res.json();
      setSchedules(data.schedules || []);
    } catch {
      setSchedules([]);
      setListError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupConfigs = async () => {
    setConfigsLoading(true);
    setConfigsError(false);
    try {
      const res = await fetch('/api/v1/backups/configs', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Config load failed');
      const data = await res.json();
      setBackupConfigs(Array.isArray(data) ? data : []);
    } catch {
      setBackupConfigs([]);
      setConfigsError(true);
    } finally {
      setConfigsLoading(false);
    }
  };

  // Helper functions (same logic as before)
  const fetchCronExplanation = async (expression: string) => {
    if (!expression || expression.split(' ').length !== 5) return setCronExplanation('');
    try {
      const res = await fetch(
        `/api/v1/schedules/explain-cron?expression=${encodeURIComponent(expression)}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      if (res.ok) setCronExplanation((await res.json()).explanation || '');
    } catch {
      // Ignore errors
    }
  };

  const handleSave = async (isEdit: boolean) => {
    if (configsLoading || configsError) return;
    if (!selectedBackupConfig) {
      setError(t('schedules.errors.selectConfig'));
      return;
    }
    if (conflictWarnings.length > 0 && !acknowledgedConflicts) {
      setError(t('schedules.errors.acknowledgeConflicts'));
      return;
    }

    if (scheduleType === 'ONETIME' && !oneTimeRunAt) {
      setError(t('schedules.errors.selectDateTime'));
      return;
    }
    if (scheduleType === 'ONETIME') {
      try {
        scheduleTimeToInstant(oneTimeRunAt, timezone);
      } catch {
        setError(t('scheduleInvalidWallTime'));
        return;
      }
    }
    setSaving(true);
    setError('');

    try {
      const payload: {
        backup_config_id: string;
        schedule_type: string;
        timezone: string;
        priority: number;
        is_enabled: boolean;
        interval_value?: string;
        cron_expression?: string;
        onetime_run_at?: string;
      } = {
        backup_config_id: selectedBackupConfig,
        schedule_type: scheduleType,
        timezone,
        priority,
        is_enabled: isEnabled,
      };

      if (scheduleType === 'INTERVAL') payload.interval_value = intervalValue;
      else if (scheduleType === 'CRON') payload.cron_expression = cronExpression;
      else if (scheduleType === 'ONETIME') {
        if (!oneTimeRunAt) throw new Error(t('schedules.errors.selectDateTime'));
        payload.onetime_run_at = scheduleTimeToInstant(oneTimeRunAt, timezone);
      }

      const url = isEdit ? `/api/v1/schedules/${selectedSchedule!.id}` : '/api/v1/schedules';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Schedule save failed');

      setSuccessMessage(
        t('schedules.success.saved', {
          action: isEdit ? t('schedules.updated') : t('schedules.created'),
        })
      );
      setShowCreateModal(false);
      setShowEditModal(false);
      resetForm();
      fetchSchedules();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch {
      setError(t('scheduleSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    timezoneTouchedRef.current = false;
    setSelectedBackupConfig('');
    setScheduleType('INTERVAL');
    setIntervalValue('6h');
    setCronExpression('0 2 * * *');
    setOneTimeRunAt('');
    setTimezone(defaultTimezone);
    setPriority(5);
    setIsEnabled(true);
    setError('');
    setConflictWarnings([]);
    setAcknowledgedConflicts(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleToggleEnabled = async (schedule: Schedule) => {
    if (actionPending) return;
    setActionPending(true);
    setError('');
    setSuccessMessage('');
    try {
      // Use dedicated pause/resume endpoints
      const endpoint = schedule.is_enabled
        ? `/api/v1/schedules/${schedule.id}/pause`
        : `/api/v1/schedules/${schedule.id}/resume`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (!res.ok) throw new Error('Schedule update failed');

      setSuccessMessage(
        t('schedules.success.toggled', {
          action: schedule.is_enabled ? t('schedules.paused') : t('schedules.started'),
        })
      );
      await fetchSchedules();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setError(t('schedules.errors.updateFailed'));
    } finally {
      setActionPending(false);
    }
  };

  const handleDelete = async (schedule: Schedule) => {
    if (actionPending) return;
    const confirmed = await showConfirm(
      t('schedules.deleteConfirmTitle'),
      t('schedules.deleteConfirmMessage', { name: schedule.backup_config_name }),
      'warning'
    );
    if (!confirmed) return;
    setActionPending(true);
    setError('');
    setSuccessMessage('');

    try {
      const res = await fetch(`/api/v1/schedules/${schedule.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (!res.ok) throw new Error('Schedule delete failed');

      setSuccessMessage(t('schedules.success.deleted'));
      await fetchSchedules();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setError(t('schedules.errors.deleteFailed'));
    } finally {
      setActionPending(false);
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setSelectedBackupConfig(schedule.backup_config_id);
    setScheduleType(schedule.schedule_type as 'INTERVAL' | 'CRON' | 'ONETIME');
    setIntervalValue(schedule.interval_value || '6h');
    setCronExpression(schedule.cron_expression || '0 2 * * *');
    setOneTimeRunAt(
      schedule.onetime_run_at
        ? instantToScheduleTime(schedule.onetime_run_at, schedule.timezone)
        : ''
    );
    setTimezone(schedule.timezone);
    setPriority(schedule.priority);
    setIsEnabled(schedule.is_enabled);
    setError('');
    setConflictWarnings([]);
    setAcknowledgedConflicts(false);
    setShowEditModal(true);
  };

  // Grouping & Helpers
  const groupSchedulesByAgent = (schedules: Schedule[]) => {
    const groups: Record<string, Schedule[]> = {};
    schedules.forEach((s) => {
      const name = s.agent_name || t('schedules.unknownAgent');
      if (!groups[name]) groups[name] = [];
      groups[name].push(s);
    });
    return groups;
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('schedules.title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('schedules.subtitle')}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="bg-surface-2-light dark:bg-surface-2-dark p-1 rounded-xl flex">
              <button
                type="button"
                aria-label={t('schedules.listView')}
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={`min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg transition-all ${viewMode === 'list' ? 'bg-surface-card-light dark:bg-surface-card-dark shadow-sm text-brand-600 dark:text-brand-400' : 'text-text-muted-light dark:text-text-muted-dark hover:text-slate-700 dark:hover:text-slate-300'}`}
              >
                <List size={20} />
              </button>
              <button
                type="button"
                aria-label={t('schedules.calendarUnavailable')}
                title={t('schedules.calendarUnavailable')}
                disabled
                className="min-h-11 min-w-11 inline-flex cursor-not-allowed items-center justify-center rounded-lg text-text-subtle-light opacity-50 dark:text-text-subtle-dark"
              >
                <Grid size={20} />
              </button>
            </div>
            <Button onClick={openCreateModal}>
              <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('schedules.newButton')}
            </Button>
          </div>
        </div>

        {error && !showCreateModal && !showEditModal && (
          <div className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl font-bold">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="p-4 bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-xl font-bold">
            {successMessage}
          </div>
        )}

        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          </div>
        ) : listError ? (
          <div
            role="alert"
            className="rounded-xl bg-state-danger-bg-light p-6 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
          >
            <p className="mb-4">{t('scheduleListLoadError')}</p>
            <Button variant="outline" onClick={fetchSchedules}>
              {t('common:actions.retry')}
            </Button>
          </div>
        ) : schedules.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <Clock
              size={48}
              className="mx-auto text-surface-border-light dark:text-surface-border-dark mb-4"
            />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('schedules.noSchedulesTitle')}
            </h3>
            <Button variant="outline" className="mt-6" onClick={openCreateModal}>
              {t('schedules.createButton')}
            </Button>
          </Card>
        ) : viewMode === 'list' ? (
          <div className="space-y-6">
            {Object.entries(groupSchedulesByAgent(schedules)).map(([agentName, agentSchedules]) => (
              <Card key={agentName} className="overflow-hidden">
                <button
                  type="button"
                  aria-expanded={!collapsedAgents.has(agentName)}
                  className="w-full px-4 py-4 sm:px-6 bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark flex justify-between items-center text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 dark:focus-visible:ring-brand-400"
                  onClick={() => {
                    const newSet = new Set(collapsedAgents);
                    if (newSet.has(agentName)) newSet.delete(agentName);
                    else newSet.add(agentName);
                    setCollapsedAgents(newSet);
                  }}
                >
                  <h3 className="font-black text-slate-700 dark:text-slate-200">{agentName}</h3>
                  <Badge variant="neutral">
                    {t('schedules.scheduleCount', { count: agentSchedules.length })}
                  </Badge>
                </button>
                {!collapsedAgents.has(agentName) && (
                  <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {agentSchedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="p-4 sm:p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between group hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                      >
                        <div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                            <h4 className="font-bold text-lg text-slate-900 dark:text-white">
                              {schedule.backup_config_name}
                            </h4>
                            <Badge variant={schedule.is_enabled ? 'success' : 'warning'}>
                              {schedule.is_enabled ? t('schedules.active') : t('schedules.paused')}
                            </Badge>
                            <Badge variant="info" className="font-mono">
                              {formatScheduleType(schedule.schedule_type)}
                            </Badge>
                          </div>
                          <div className="text-sm text-text-muted-light dark:text-text-muted-dark font-medium flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                            <span className="flex items-center gap-1">
                              <Clock size={14} /> {t('schedules.nextRun')}{' '}
                              {formatDateTime(schedule.next_run_at, schedule.timezone)}
                            </span>
                            <span>
                              {t('schedules.timezoneLabel')} {schedule.timezone}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-11 w-11"
                            aria-label={t('schedules.actions.edit')}
                            onClick={() => handleEdit(schedule)}
                            title={t('schedules.actions.edit')}
                          >
                            <Edit2
                              size={18}
                              className="text-state-info-light dark:text-state-info-dark"
                            />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-11 w-11"
                            aria-label={
                              schedule.is_enabled
                                ? t('schedules.actions.pause')
                                : t('schedules.actions.resume')
                            }
                            onClick={() => handleToggleEnabled(schedule)}
                            disabled={actionPending}
                            title={
                              schedule.is_enabled
                                ? t('schedules.actions.pause')
                                : t('schedules.actions.resume')
                            }
                          >
                            {schedule.is_enabled ? (
                              <Pause
                                size={18}
                                className="text-state-warn-light dark:text-state-warn-dark"
                              />
                            ) : (
                              <Play
                                size={18}
                                className="text-state-ok-light dark:text-state-ok-dark"
                              />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-11 w-11"
                            aria-label={t('schedules.actions.delete')}
                            onClick={() => handleDelete(schedule)}
                            disabled={actionPending}
                            title={t('schedules.actions.delete')}
                          >
                            <Trash2
                              size={18}
                              className="text-state-danger-light dark:text-state-danger-dark"
                            />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6 h-[300px] flex items-center justify-center text-center text-text-subtle-light dark:text-text-subtle-dark font-medium border-2 border-dashed">
            {t('schedules.calendarUnavailable')}
          </Card>
        )}
      </div>

      {/* Simplified Modals - Implementation similar to other pages using Card & Input */}
      {showCreateModal && (
        <div
          role="dialog"
          ref={scheduleDialogRef}
          tabIndex={-1}
          aria-modal="true"
          aria-labelledby="schedule-create-title"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
        >
          <Card className="w-full max-w-lg shadow-elevated max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="p-5 sm:p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h2
                  id="schedule-create-title"
                  className="text-2xl font-black text-slate-900 dark:text-white tracking-tight"
                >
                  {t('schedules.createTitle')}
                </h2>
                <button
                  type="button"
                  aria-label={t('common:actions.close')}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center"
                  disabled={saving}
                  onClick={() => setShowCreateModal(false)}
                >
                  <X className="text-text-subtle-light dark:text-text-subtle-dark" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave(false);
                }}
                className="space-y-6"
              >
                <fieldset disabled={saving} className="space-y-4">
                  {error && (
                    <p
                      role="alert"
                      className="rounded-xl bg-state-danger-bg-light p-4 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                    >
                      {error}
                    </p>
                  )}
                  {configsError && (
                    <div
                      role="alert"
                      className="rounded-xl bg-state-danger-bg-light p-4 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                    >
                      <p className="mb-3">{t('scheduleConfigsLoadError')}</p>
                      <Button type="button" variant="outline" onClick={fetchBackupConfigs}>
                        {t('common:actions.retry')}
                      </Button>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="schedule-create-backup-config"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.backupConfig')}
                    </label>
                    <select
                      id="schedule-create-backup-config"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                      value={selectedBackupConfig}
                      onChange={(e) => setSelectedBackupConfig(e.target.value)}
                    >
                      <option value="">{t('schedules.labels.selectConfig')}</option>
                      {backupConfigs.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.type})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark mt-1">
                      {t('schedules.labels.selectConfigHint')}
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="schedule-create-type"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.type')}
                    </label>
                    <select
                      id="schedule-create-type"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={scheduleType}
                      onChange={(e) =>
                        setScheduleType(e.target.value as 'INTERVAL' | 'CRON' | 'ONETIME')
                      }
                    >
                      <option value="INTERVAL">{t('schedules.types.interval')}</option>
                      <option value="CRON">{t('schedules.types.cron')}</option>
                      <option value="ONETIME">{t('schedules.types.oneTime')}</option>
                    </select>
                  </div>

                  {scheduleType === 'INTERVAL' && (
                    <ScheduleIntervalSelect
                      value={intervalValue}
                      onChange={(value) => setIntervalValue(value)}
                      label={t('schedules.labels.interval')}
                      id="schedule-create-interval"
                    />
                  )}

                  {scheduleType === 'CRON' && (
                    <div>
                      <Input
                        id="schedule-create-cron"
                        label={t('schedules.labels.cronExpression')}
                        value={cronExpression}
                        onChange={(e) => setCronExpression(e.target.value)}
                        placeholder="0 2 * * *"
                        error={cronValidation.error || undefined}
                      />
                      {cronValidation.isValid && cronValidation.intervalMinutes && (
                        <p className="text-xs text-brand-600 dark:text-brand-400 mt-2 font-medium bg-accent-soft-light dark:bg-accent-soft-dark p-2 rounded-lg">
                          {t('schedules.cronIntervalSummary', {
                            interval: new Intl.NumberFormat(locale, {
                              style: 'unit',
                              unit: 'minute',
                              unitDisplay: 'long',
                            }).format(cronValidation.intervalMinutes),
                          })}
                        </p>
                      )}
                    </div>
                  )}

                  {scheduleType === 'ONETIME' && (
                    <Input
                      id="schedule-create-run-at"
                      label={t('schedules.labels.runAt')}
                      type="datetime-local"
                      value={oneTimeRunAt}
                      onChange={(e) => setOneTimeRunAt(e.target.value)}
                    />
                  )}

                  {/* Timezone Selector */}
                  <div>
                    <label
                      htmlFor="schedule-create-timezone"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.timezone')}
                    </label>
                    <select
                      id="schedule-create-timezone"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={timezone}
                      onChange={(e) => {
                        timezoneTouchedRef.current = true;
                        setTimezone(e.target.value);
                      }}
                    >
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {t(`schedules.timezones.${tz.key}`)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark mt-1">
                      {t('schedules.timezoneHelp')}
                    </p>
                  </div>

                  {/* Conflict Warnings */}
                  {conflictWarnings.length > 0 && !acknowledgedConflicts && (
                    <div className="p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl">
                      <div className="flex gap-2 text-state-warn-light dark:text-state-warn-dark font-bold mb-2">
                        <AlertTriangle size={18} /> {t('schedules.conflicts.detected')}
                      </div>
                      <ul className="list-disc pl-5 text-xs text-state-warn-light dark:text-state-warn-dark space-y-1 mb-3">
                        {conflictWarnings.map((w: any, i) => (
                          <li key={i}>{w.message}</li>
                        ))}
                      </ul>
                      <label className="flex items-center gap-2 text-xs font-bold text-state-warn-light dark:text-state-warn-dark cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acknowledgedConflicts}
                          onChange={(e) => setAcknowledgedConflicts(e.target.checked)}
                          className="rounded text-state-warn-light dark:text-state-warn-dark"
                        />
                        {t('schedules.conflicts.acknowledge')}
                      </label>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                    <Button
                      variant="secondary"
                      className="flex-1"
                      disabled={saving}
                      onClick={() => setShowCreateModal(false)}
                      type="button"
                    >
                      {t('schedules.buttons.cancel')}
                    </Button>
                    <Button
                      className="flex-1"
                      type="submit"
                      isLoading={saving}
                      disabled={
                        configsLoading ||
                        configsError ||
                        (scheduleType === 'CRON' && !cronValidation.isValid)
                      }
                    >
                      {t('schedules.buttons.create')}
                    </Button>
                  </div>
                </fieldset>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showEditModal && selectedSchedule && (
        <div
          role="dialog"
          ref={scheduleDialogRef}
          tabIndex={-1}
          aria-modal="true"
          aria-labelledby="schedule-edit-title"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
        >
          <Card className="w-full max-w-lg shadow-elevated max-h-[calc(100dvh-2rem)] overflow-y-auto">
            <div className="p-5 sm:p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h2
                  id="schedule-edit-title"
                  className="text-2xl font-black text-slate-900 dark:text-white tracking-tight"
                >
                  {t('schedules.editTitle')}
                </h2>
                <button
                  type="button"
                  aria-label={t('common:actions.close')}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center"
                  disabled={saving}
                  onClick={() => setShowEditModal(false)}
                >
                  <X className="text-text-subtle-light dark:text-text-subtle-dark" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave(true);
                }}
                className="space-y-6"
              >
                <fieldset disabled={saving} className="space-y-4">
                  {error && (
                    <p
                      role="alert"
                      className="rounded-xl bg-state-danger-bg-light p-4 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                    >
                      {error}
                    </p>
                  )}
                  {configsError && (
                    <div
                      role="alert"
                      className="rounded-xl bg-state-danger-bg-light p-4 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                    >
                      <p className="mb-3">{t('scheduleConfigsLoadError')}</p>
                      <Button type="button" variant="outline" onClick={fetchBackupConfigs}>
                        {t('common:actions.retry')}
                      </Button>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="schedule-edit-backup-config"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.backupConfig')}
                    </label>
                    <select
                      id="schedule-edit-backup-config"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={selectedBackupConfig}
                      onChange={(e) => setSelectedBackupConfig(e.target.value)}
                    >
                      <option value="">{t('schedules.labels.selectConfig')}</option>
                      {backupConfigs.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.type})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="schedule-edit-type"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.type')}
                    </label>
                    <select
                      id="schedule-edit-type"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={scheduleType}
                      onChange={(e) =>
                        setScheduleType(e.target.value as 'INTERVAL' | 'CRON' | 'ONETIME')
                      }
                    >
                      <option value="INTERVAL">{t('schedules.types.interval')}</option>
                      <option value="CRON">{t('schedules.types.cron')}</option>
                      <option value="ONETIME">{t('schedules.types.oneTime')}</option>
                    </select>
                  </div>

                  {scheduleType === 'INTERVAL' && (
                    <ScheduleIntervalSelect
                      value={intervalValue}
                      onChange={(value) => setIntervalValue(value)}
                      label={t('schedules.labels.interval')}
                      id="schedule-edit-interval"
                    />
                  )}

                  {scheduleType === 'CRON' && (
                    <div>
                      <Input
                        id="schedule-edit-cron"
                        label={t('schedules.labels.cronExpression')}
                        value={cronExpression}
                        onChange={(e) => setCronExpression(e.target.value)}
                        placeholder="0 2 * * *"
                        error={cronValidation.error || undefined}
                      />
                      {cronValidation.isValid && cronValidation.intervalMinutes && (
                        <p className="text-xs text-brand-600 dark:text-brand-400 mt-2 font-medium bg-accent-soft-light dark:bg-accent-soft-dark p-2 rounded-lg">
                          {t('schedules.cronIntervalSummary', {
                            interval: new Intl.NumberFormat(locale, {
                              style: 'unit',
                              unit: 'minute',
                              unitDisplay: 'long',
                            }).format(cronValidation.intervalMinutes),
                          })}
                        </p>
                      )}
                    </div>
                  )}

                  {scheduleType === 'ONETIME' && (
                    <Input
                      id="schedule-edit-run-at"
                      label={t('schedules.labels.runAt')}
                      type="datetime-local"
                      value={oneTimeRunAt}
                      onChange={(e) => setOneTimeRunAt(e.target.value)}
                    />
                  )}

                  {/* Timezone Selector */}
                  <div>
                    <label
                      htmlFor="schedule-edit-timezone"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.timezone')}
                    </label>
                    <select
                      id="schedule-edit-timezone"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                    >
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {t(`schedules.timezones.${tz.key}`)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-text-subtle-light dark:text-text-subtle-dark mt-1">
                      {t('schedules.timezoneHelp')}
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="schedule-edit-status"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('schedules.labels.status')}
                    </label>
                    <select
                      id="schedule-edit-status"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={isEnabled ? 'true' : 'false'}
                      onChange={(e) => setIsEnabled(e.target.value === 'true')}
                    >
                      <option value="true">{t('schedules.active')}</option>
                      <option value="false">{t('schedules.paused')}</option>
                    </select>
                  </div>

                  {/* Conflict Warnings */}
                  {conflictWarnings.length > 0 && !acknowledgedConflicts && (
                    <div className="p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-xl">
                      <div className="flex gap-2 text-state-warn-light dark:text-state-warn-dark font-bold mb-2">
                        <AlertTriangle size={18} /> {t('schedules.conflicts.detected')}
                      </div>
                      <ul className="list-disc pl-5 text-xs text-state-warn-light dark:text-state-warn-dark space-y-1 mb-3">
                        {conflictWarnings.map((w: any, i) => (
                          <li key={i}>{w.message}</li>
                        ))}
                      </ul>
                      <label className="flex items-center gap-2 text-xs font-bold text-state-warn-light dark:text-state-warn-dark cursor-pointer">
                        <input
                          type="checkbox"
                          checked={acknowledgedConflicts}
                          onChange={(e) => setAcknowledgedConflicts(e.target.checked)}
                          className="rounded text-state-warn-light dark:text-state-warn-dark"
                        />
                        {t('schedules.conflicts.acknowledge')}
                      </label>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
                    <Button
                      variant="secondary"
                      className="flex-1"
                      disabled={saving}
                      onClick={() => setShowEditModal(false)}
                      type="button"
                    >
                      {t('schedules.buttons.cancel')}
                    </Button>
                    <Button
                      className="flex-1"
                      type="submit"
                      isLoading={saving}
                      disabled={
                        configsLoading ||
                        configsError ||
                        (scheduleType === 'CRON' && !cronValidation.isValid)
                      }
                    >
                      {t('schedules.buttons.save')}
                    </Button>
                  </div>
                </fieldset>
              </form>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
